/**
 * WorkflowActionEditors.js
 * 
 * Action configuration editors for workflow builder with real data integration.
 * Includes editors for:
 * - Portal Access (Grant/Revoke)
 * - Offer/Product Access (Grant/Revoke)
 * - Tags (Apply/Remove)
 * - Wait/Delay
 * - Conditions
 * - Internal Notifications
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useActionEditorDraft } from '../../hooks/useActionEditorDraft';
import { X, Search, Plus, Tag, Key, Shield, Gift, Clock, Bell, Check, Loader2, GitBranch } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '../../components/ui/command';
import { cn } from '../../lib/utils';
import WorkflowStepEditorShell from './WorkflowStepEditorShell';

const DARK_STEP_SHELL_CLASS = 'bg-zinc-900 border-l border-zinc-700';
const STEP_SHELL_PROPS = 'width, overlayMode, onOverlayDismiss, activePanelType, chromeHeight';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function useEditorCloseGuard(editorKey, isOpen, step, draft, mapStepToDraft, onClose) {
  const { guardedClose, commitDraft } = useActionEditorDraft({
    editorId: step ? `${editorKey}-${step.id}` : editorKey,
    isOpen,
    step,
    draft,
    mapStepToDraft,
  });
  const requestClose = useCallback(() => guardedClose(onClose), [guardedClose, onClose]);
  return { requestClose, commitDraft };
}

// =====================================================
// GRANT PORTAL ACCESS EDITOR
// =====================================================
export const GrantPortalAccessEditor = ({ isOpen, onClose, step, onSave, token , width, overlayMode, onOverlayDismiss, activePanelType = 'grant-portal', chromeHeight }) => {
  const [accessLevel, setAccessLevel] = useState(step?.config?.accessLevel || 'client');
  const [sendInvite, setSendInvite] = useState(step?.config?.sendInvite ?? true);
  const [actionName, setActionName] = useState(step?.config?.actionName || 'Grant Portal Access');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (step) {
      setAccessLevel(step.config?.accessLevel || 'client');
      setSendInvite(step.config?.sendInvite ?? true);
      setActionName(step.config?.actionName || 'Grant Portal Access');
    }
  }, [step]);

  const draft = useMemo(
    () => ({ accessLevel, sendInvite, actionName }),
    [accessLevel, sendInvite, actionName],
  );
  const mapStepToDraft = useCallback((s) => ({
    accessLevel: s?.config?.accessLevel || 'client',
    sendInvite: s?.config?.sendInvite ?? true,
    actionName: s?.config?.actionName || 'Grant Portal Access',
  }), []);
  const { requestClose, commitDraft } = useEditorCloseGuard(
    'grant-portal',
    isOpen,
    step,
    draft,
    mapStepToDraft,
    onClose,
  );

  if (!isOpen || !step) return null;

  const handleSave = () => {
    onSave({
      ...step,
      config: {
        ...step.config,
        accessLevel,
        sendInvite,
        actionName,
        configured: true,
      },
    });
    commitDraft();
    onClose();
  };

  const accessLevels = [
    { value: 'client', label: 'Client Portal - Basic', description: 'Standard client portal access' },
    { value: 'client_premium', label: 'Client Portal - Premium', description: 'Premium features + AI assistance' },
    { value: 'self_repair', label: 'Self Repair Access', description: 'DIY credit repair portal' },
    { value: 'self_repair_premium', label: 'Self Repair Premium', description: 'Premium DIY with AI guidance' },
  ];

  return (
    <WorkflowStepEditorShell
      isOpen={isOpen}
      width={width}
      overlayMode={overlayMode}
      onOverlayDismiss={onOverlayDismiss}
      activePanelType={activePanelType}
      chromeHeight={chromeHeight}
      className={DARK_STEP_SHELL_CLASS}
      testId="grant-portal-access"
    >
      <div className="p-4 border-b border-zinc-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
            <Key size={20} className="text-emerald-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Grant Portal Access</h3>
            <p className="text-xs text-zinc-500">Enable client portal for contact</p>
          </div>
        </div>
        <button onClick={requestClose} className="p-2 hover:bg-zinc-800 rounded-lg">
          <X size={18} className="text-zinc-400" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Action Name */}
        <div>
          <Label className="text-zinc-400 text-sm">Action Name</Label>
          <Input
            value={actionName}
            onChange={(e) => setActionName(e.target.value)}
            placeholder="Grant Portal Access"
            className="mt-2 bg-zinc-800 border-zinc-700 text-white"
          />
        </div>

        {/* Access Level */}
        <div>
          <Label className="text-zinc-400 text-sm">Access Level *</Label>
          <div className="mt-2 space-y-2">
            {accessLevels.map((level) => (
              <button
                key={level.value}
                onClick={() => setAccessLevel(level.value)}
                className={cn(
                  'w-full flex items-center justify-between p-3 rounded-lg border transition-all text-left',
                  accessLevel === level.value
                    ? 'bg-emerald-500/10 border-emerald-500/50 text-white'
                    : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                )}
              >
                <div>
                  <p className="font-medium text-sm">{level.label}</p>
                  <p className="text-xs text-zinc-500">{level.description}</p>
                </div>
                {accessLevel === level.value && (
                  <Check size={18} className="text-emerald-400" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Send Invite Toggle */}
        <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
          <div>
            <p className="text-sm font-medium text-white">Send Portal Invite Email</p>
            <p className="text-xs text-zinc-500">Email contact with login instructions</p>
          </div>
          <Switch
            checked={sendInvite}
            onCheckedChange={setSendInvite}
          />
        </div>
      </div>

      <div className="p-4 border-t border-zinc-700 flex justify-end gap-2">
        <Button variant="outline" onClick={requestClose} className="border-zinc-600 text-zinc-300">
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          className="bg-emerald-500 hover:bg-emerald-600 text-white"
          disabled={!accessLevel}
        >
          Save Action
        </Button>
      </div>
    </WorkflowStepEditorShell>
  );
};

// =====================================================
// REVOKE PORTAL ACCESS EDITOR
// =====================================================
export const RevokePortalAccessEditor = ({ isOpen, onClose, step, onSave, token , width, overlayMode, onOverlayDismiss, activePanelType = 'revoke-portal', chromeHeight }) => {
  const [actionName, setActionName] = useState(step?.config?.actionName || 'Revoke Portal Access');
  const [revokeReason, setRevokeReason] = useState(step?.config?.revokeReason || '');

  useEffect(() => {
    if (step) {
      setActionName(step.config?.actionName || 'Revoke Portal Access');
      setRevokeReason(step.config?.revokeReason || '');
    }
  }, [step]);

  if (!isOpen || !step) return null;

  const handleSave = () => {
    onSave({
      ...step,
      config: {
        ...step.config,
        actionName,
        revokeReason,
        configured: true,
      },
    });
    onClose();
  };

  return (
    <WorkflowStepEditorShell
      isOpen={isOpen}
      width={width}
      overlayMode={overlayMode}
      onOverlayDismiss={onOverlayDismiss}
      activePanelType={activePanelType}
      chromeHeight={chromeHeight}
      className={DARK_STEP_SHELL_CLASS}
      testId="revoke-portal-access"
    >
      <div className="p-4 border-b border-zinc-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
            <Shield size={20} className="text-red-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Revoke Portal Access</h3>
            <p className="text-xs text-zinc-500">Disable portal access for contact</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
          <X size={18} className="text-zinc-400" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Action Name */}
        <div>
          <Label className="text-zinc-400 text-sm">Action Name</Label>
          <Input
            value={actionName}
            onChange={(e) => setActionName(e.target.value)}
            placeholder="Revoke Portal Access"
            className="mt-2 bg-zinc-800 border-zinc-700 text-white"
          />
        </div>

        {/* Revoke Reason */}
        <div>
          <Label className="text-zinc-400 text-sm">Revoke Reason (optional)</Label>
          <Textarea
            value={revokeReason}
            onChange={(e) => setRevokeReason(e.target.value)}
            placeholder="Enter reason for revoking access..."
            className="mt-2 bg-zinc-800 border-zinc-700 text-white min-h-[100px]"
          />
        </div>

        {/* Warning */}
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-sm text-red-400">
            This action will immediately lock the contact's portal access. 
            The contact record and history will be preserved.
          </p>
        </div>
      </div>

      <div className="p-4 border-t border-zinc-700 flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} className="border-zinc-600 text-zinc-300">
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          className="bg-red-500 hover:bg-red-600 text-white"
        >
          Save Action
        </Button>
      </div>
    </WorkflowStepEditorShell>
  );
};

// =====================================================
// GRANT OFFER ACCESS EDITOR
// =====================================================
export const GrantOfferAccessEditor = ({ isOpen, onClose, step, onSave, token , width, overlayMode, onOverlayDismiss, activePanelType = 'grant-offer', chromeHeight }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState(step?.config?.offerId || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [actionName, setActionName] = useState(step?.config?.actionName || 'Grant Offer / Product');

  useEffect(() => {
    if (step) {
      setSelectedOffer(step.config?.offerId || '');
      setActionName(step.config?.actionName || 'Grant Offer / Product');
    }
  }, [step]);

  // Fetch real offers from backend
  useEffect(() => {
    const fetchOffers = async () => {
      if (!token) return;
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/workflow-actions/offers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setOffers(data.offers || []);
        }
      } catch (err) {
        console.error('Failed to fetch offers:', err);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchOffers();
    }
  }, [isOpen, token]);

  if (!isOpen || !step) return null;

  const filteredOffers = offers.filter(o => 
    o.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    o.id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSave = () => {
    const selected = offers.find(o => o.id === selectedOffer);
    onSave({
      ...step,
      config: {
        ...step.config,
        offerId: selectedOffer,
        offerName: selected?.name || '',
        offerType: selected?.type || 'internal',
        actionName,
        configured: true,
      },
    });
    onClose();
  };

  return (
    <WorkflowStepEditorShell
      isOpen={isOpen}
      width={width}
      overlayMode={overlayMode}
      onOverlayDismiss={onOverlayDismiss}
      activePanelType={activePanelType}
      chromeHeight={chromeHeight}
      className={DARK_STEP_SHELL_CLASS}
      testId="grant-offer-access"
    >
      <div className="p-4 border-b border-zinc-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
            <Gift size={20} className="text-emerald-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Grant Offer / Product</h3>
            <p className="text-xs text-zinc-500">Grant access to offer or product</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
          <X size={18} className="text-zinc-400" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Action Name */}
        <div>
          <Label className="text-zinc-400 text-sm">Action Name</Label>
          <Input
            value={actionName}
            onChange={(e) => setActionName(e.target.value)}
            placeholder="Grant Offer / Product"
            className="mt-2 bg-zinc-800 border-zinc-700 text-white"
          />
        </div>

        {/* Offer Selector */}
        <div>
          <Label className="text-zinc-400 text-sm">Select Offer / Product *</Label>
          <div className="mt-2">
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search offers..."
                className="pl-9 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
              </div>
            ) : filteredOffers.length === 0 ? (
              <div className="text-center py-8 bg-zinc-800/50 rounded-lg">
                <Gift className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
                <p className="text-sm text-zinc-500">
                  {searchQuery ? 'No offers matching search' : 'No offers found'}
                </p>
                <p className="text-xs text-zinc-600 mt-1">
                  Sync products from Whop or create offers first
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {filteredOffers.map((offer) => (
                  <button
                    key={offer.id}
                    onClick={() => setSelectedOffer(offer.id)}
                    className={cn(
                      'w-full flex items-center justify-between p-3 rounded-lg border transition-all text-left',
                      selectedOffer === offer.id
                        ? 'bg-emerald-500/10 border-emerald-500/50 text-white'
                        : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{offer.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs bg-zinc-700/50 border-zinc-600">
                          {offer.type}
                        </Badge>
                        {offer.price_display && (
                          <span className="text-xs text-zinc-500">{offer.price_display}</span>
                        )}
                      </div>
                    </div>
                    {selectedOffer === offer.id && (
                      <Check size={18} className="text-emerald-400 flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 border-t border-zinc-700 flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} className="border-zinc-600 text-zinc-300">
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          className="bg-emerald-500 hover:bg-emerald-600 text-white"
          disabled={!selectedOffer}
        >
          Save Action
        </Button>
      </div>
    </WorkflowStepEditorShell>
  );
};

// =====================================================
// REVOKE OFFER ACCESS EDITOR
// =====================================================
export const RevokeOfferAccessEditor = ({ isOpen, onClose, step, onSave, token , width, overlayMode, onOverlayDismiss, activePanelType = 'revoke-offer', chromeHeight }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState(step?.config?.offerId || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [actionName, setActionName] = useState(step?.config?.actionName || 'Revoke Offer / Product');

  useEffect(() => {
    if (step) {
      setSelectedOffer(step.config?.offerId || '');
      setActionName(step.config?.actionName || 'Revoke Offer / Product');
    }
  }, [step]);

  // Fetch real offers from backend
  useEffect(() => {
    const fetchOffers = async () => {
      if (!token) return;
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/workflow-actions/offers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setOffers(data.offers || []);
        }
      } catch (err) {
        console.error('Failed to fetch offers:', err);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchOffers();
    }
  }, [isOpen, token]);

  if (!isOpen || !step) return null;

  const filteredOffers = offers.filter(o => 
    o.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    o.id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSave = () => {
    const selected = offers.find(o => o.id === selectedOffer);
    onSave({
      ...step,
      config: {
        ...step.config,
        offerId: selectedOffer,
        offerName: selected?.name || '',
        offerType: selected?.type || 'internal',
        actionName,
        configured: true,
      },
    });
    onClose();
  };

  return (
    <WorkflowStepEditorShell
      isOpen={isOpen}
      width={width}
      overlayMode={overlayMode}
      onOverlayDismiss={onOverlayDismiss}
      activePanelType={activePanelType}
      chromeHeight={chromeHeight}
      className={DARK_STEP_SHELL_CLASS}
      testId="revoke-offer-access"
    >
      <div className="p-4 border-b border-zinc-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
            <Gift size={20} className="text-red-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Revoke Offer / Product</h3>
            <p className="text-xs text-zinc-500">Remove offer or product access</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
          <X size={18} className="text-zinc-400" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Action Name */}
        <div>
          <Label className="text-zinc-400 text-sm">Action Name</Label>
          <Input
            value={actionName}
            onChange={(e) => setActionName(e.target.value)}
            placeholder="Revoke Offer / Product"
            className="mt-2 bg-zinc-800 border-zinc-700 text-white"
          />
        </div>

        {/* Offer Selector */}
        <div>
          <Label className="text-zinc-400 text-sm">Select Offer / Product to Revoke *</Label>
          <div className="mt-2">
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search offers..."
                className="pl-9 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
              </div>
            ) : filteredOffers.length === 0 ? (
              <div className="text-center py-8 bg-zinc-800/50 rounded-lg">
                <Gift className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
                <p className="text-sm text-zinc-500">No offers found</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {filteredOffers.map((offer) => (
                  <button
                    key={offer.id}
                    onClick={() => setSelectedOffer(offer.id)}
                    className={cn(
                      'w-full flex items-center justify-between p-3 rounded-lg border transition-all text-left',
                      selectedOffer === offer.id
                        ? 'bg-red-500/10 border-red-500/50 text-white'
                        : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                    )}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{offer.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs bg-zinc-700/50 border-zinc-600">
                          {offer.type}
                        </Badge>
                      </div>
                    </div>
                    {selectedOffer === offer.id && (
                      <Check size={18} className="text-red-400 flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 border-t border-zinc-700 flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} className="border-zinc-600 text-zinc-300">
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          className="bg-red-500 hover:bg-red-600 text-white"
          disabled={!selectedOffer}
        >
          Save Action
        </Button>
      </div>
    </WorkflowStepEditorShell>
  );
};

// =====================================================
// APPLY TAG EDITOR (with real tags from API)
// =====================================================
export const ApplyTagEditor = ({ isOpen, onClose, step, onSave, token, agencyId , width, overlayMode, onOverlayDismiss, activePanelType = 'apply-tag', chromeHeight }) => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTags, setSelectedTags] = useState(step?.config?.tags || []);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionName, setActionName] = useState(step?.config?.actionName || 'Apply Tag');
  const [createNewTag, setCreateNewTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');

  useEffect(() => {
    if (step) {
      setSelectedTags(step.config?.tags || []);
      setActionName(step.config?.actionName || 'Apply Tag');
    }
  }, [step]);

  // Fetch real tags from backend
  useEffect(() => {
    const fetchTags = async () => {
      if (!token) return;
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (agencyId) params.append('agency_id', agencyId);
        
        const res = await fetch(`${API_URL}/api/workflow-actions/tags?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setTags(data.tags || []);
        }
      } catch (err) {
        console.error('Failed to fetch tags:', err);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchTags();
    }
  }, [isOpen, token, agencyId]);

  if (!isOpen || !step) return null;

  const filteredTags = tags.filter(t => 
    t.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleTag = (tagId) => {
    setSelectedTags(prev => 
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    
    try {
      const res = await fetch(`${API_URL}/api/workflow-actions/tags?name=${encodeURIComponent(newTagName)}&agency_id=${agencyId || ''}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.tag) {
          setTags(prev => [...prev, data.tag]);
          setSelectedTags(prev => [...prev, data.tag.id]);
        }
        setNewTagName('');
        setCreateNewTag(false);
      }
    } catch (err) {
      console.error('Failed to create tag:', err);
    }
  };

  const handleSave = () => {
    const selectedTagNames = tags
      .filter(t => selectedTags.includes(t.id))
      .map(t => t.name);
    
    onSave({
      ...step,
      config: {
        ...step.config,
        tags: selectedTags,
        tagNames: selectedTagNames,
        actionName,
        configured: selectedTags.length > 0,
      },
    });
    onClose();
  };

  // Check if search query doesn't match any existing tag
  const showCreateOption = searchQuery && !tags.some(t => 
    t.name?.toLowerCase() === searchQuery.toLowerCase()
  );

  return (
    <WorkflowStepEditorShell
      isOpen={isOpen}
      width={width}
      overlayMode={overlayMode}
      onOverlayDismiss={onOverlayDismiss}
      activePanelType={activePanelType}
      chromeHeight={chromeHeight}
      className={DARK_STEP_SHELL_CLASS}
      testId="apply-tag-editor"
    >
      <div className="p-4 border-b border-zinc-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-pink-500/20 flex items-center justify-center">
            <Tag size={20} className="text-pink-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Apply Tag</h3>
            <p className="text-xs text-zinc-500">Add tags to contact</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
          <X size={18} className="text-zinc-400" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Action Name */}
        <div>
          <Label className="text-zinc-400 text-sm">Action Name</Label>
          <Input
            value={actionName}
            onChange={(e) => setActionName(e.target.value)}
            placeholder="Apply Tag"
            className="mt-2 bg-zinc-800 border-zinc-700 text-white"
          />
        </div>

        {/* Selected Tags */}
        {selectedTags.length > 0 && (
          <div>
            <Label className="text-zinc-400 text-sm">Selected Tags</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedTags.map(tagId => {
                const tag = tags.find(t => t.id === tagId);
                return (
                  <Badge 
                    key={tagId}
                    variant="outline" 
                    className="bg-pink-500/20 border-pink-500/50 text-pink-300 cursor-pointer hover:bg-pink-500/30"
                    onClick={() => toggleTag(tagId)}
                  >
                    {tag?.name || tagId}
                    <X size={12} className="ml-1" />
                  </Badge>
                );
              })}
            </div>
          </div>
        )}

        {/* Tag Selector */}
        <div>
          <Label className="text-zinc-400 text-sm">Search & Select Tags</Label>
          <div className="mt-2">
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search or type to create..."
                className="pl-9 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
              </div>
            ) : (
              <div className="space-y-1 max-h-[250px] overflow-y-auto">
                {/* Create new tag option */}
                {showCreateOption && (
                  <button
                    onClick={() => {
                      setNewTagName(searchQuery);
                      handleCreateTag();
                    }}
                    className="w-full flex items-center gap-2 p-3 rounded-lg bg-pink-500/10 border border-pink-500/30 text-pink-300 hover:bg-pink-500/20 transition-all"
                  >
                    <Plus size={16} />
                    <span className="text-sm">Create tag: "{searchQuery}"</span>
                  </button>
                )}
                
                {filteredTags.length === 0 && !showCreateOption ? (
                  <div className="text-center py-6 text-zinc-500 text-sm">
                    No tags found
                  </div>
                ) : (
                  filteredTags.map((tag) => (
                    <button
                      key={tag.id}
                      onClick={() => toggleTag(tag.id)}
                      className={cn(
                        'w-full flex items-center justify-between p-3 rounded-lg border transition-all text-left',
                        selectedTags.includes(tag.id)
                          ? 'bg-pink-500/10 border-pink-500/50 text-white'
                          : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: tag.color || '#6366f1' }}
                        />
                        <span className="text-sm">{tag.name}</span>
                        {tag.usage_count && (
                          <span className="text-xs text-zinc-500">({tag.usage_count})</span>
                        )}
                      </div>
                      {selectedTags.includes(tag.id) && (
                        <Check size={16} className="text-pink-400" />
                      )}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 border-t border-zinc-700 flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} className="border-zinc-600 text-zinc-300">
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          className="bg-pink-500 hover:bg-pink-600 text-white"
          disabled={selectedTags.length === 0}
        >
          Save Action
        </Button>
      </div>
    </WorkflowStepEditorShell>
  );
};

// =====================================================
// REMOVE TAG EDITOR
// =====================================================
export const RemoveTagEditor = ({ isOpen, onClose, step, onSave, token, agencyId , width, overlayMode, onOverlayDismiss, activePanelType = 'remove-tag', chromeHeight }) => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTags, setSelectedTags] = useState(step?.config?.tags || []);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionName, setActionName] = useState(step?.config?.actionName || 'Remove Tag');

  useEffect(() => {
    if (step) {
      setSelectedTags(step.config?.tags || []);
      setActionName(step.config?.actionName || 'Remove Tag');
    }
  }, [step]);

  // Fetch real tags from backend
  useEffect(() => {
    const fetchTags = async () => {
      if (!token) return;
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (agencyId) params.append('agency_id', agencyId);
        
        const res = await fetch(`${API_URL}/api/workflow-actions/tags?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setTags(data.tags || []);
        }
      } catch (err) {
        console.error('Failed to fetch tags:', err);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchTags();
    }
  }, [isOpen, token, agencyId]);

  if (!isOpen || !step) return null;

  const filteredTags = tags.filter(t => 
    t.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleTag = (tagId) => {
    setSelectedTags(prev => 
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const handleSave = () => {
    const selectedTagNames = tags
      .filter(t => selectedTags.includes(t.id))
      .map(t => t.name);
    
    onSave({
      ...step,
      config: {
        ...step.config,
        tags: selectedTags,
        tagNames: selectedTagNames,
        actionName,
        configured: selectedTags.length > 0,
      },
    });
    onClose();
  };

  return (
    <WorkflowStepEditorShell
      isOpen={isOpen}
      width={width}
      overlayMode={overlayMode}
      onOverlayDismiss={onOverlayDismiss}
      activePanelType={activePanelType}
      chromeHeight={chromeHeight}
      className={DARK_STEP_SHELL_CLASS}
      testId="remove-tag-editor"
    >
      <div className="p-4 border-b border-zinc-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-500/20 flex items-center justify-center">
            <Tag size={20} className="text-slate-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Remove Tag</h3>
            <p className="text-xs text-zinc-500">Remove tags from contact</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
          <X size={18} className="text-zinc-400" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Action Name */}
        <div>
          <Label className="text-zinc-400 text-sm">Action Name</Label>
          <Input
            value={actionName}
            onChange={(e) => setActionName(e.target.value)}
            placeholder="Remove Tag"
            className="mt-2 bg-zinc-800 border-zinc-700 text-white"
          />
        </div>

        {/* Selected Tags */}
        {selectedTags.length > 0 && (
          <div>
            <Label className="text-zinc-400 text-sm">Tags to Remove</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedTags.map(tagId => {
                const tag = tags.find(t => t.id === tagId);
                return (
                  <Badge 
                    key={tagId}
                    variant="outline" 
                    className="bg-red-500/20 border-red-500/50 text-red-300 cursor-pointer hover:bg-red-500/30"
                    onClick={() => toggleTag(tagId)}
                  >
                    {tag?.name || tagId}
                    <X size={12} className="ml-1" />
                  </Badge>
                );
              })}
            </div>
          </div>
        )}

        {/* Tag Selector */}
        <div>
          <Label className="text-zinc-400 text-sm">Search & Select Tags</Label>
          <div className="mt-2">
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tags..."
                className="pl-9 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
              </div>
            ) : filteredTags.length === 0 ? (
              <div className="text-center py-6 text-zinc-500 text-sm">
                No tags found
              </div>
            ) : (
              <div className="space-y-1 max-h-[250px] overflow-y-auto">
                {filteredTags.map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => toggleTag(tag.id)}
                    className={cn(
                      'w-full flex items-center justify-between p-3 rounded-lg border transition-all text-left',
                      selectedTags.includes(tag.id)
                        ? 'bg-red-500/10 border-red-500/50 text-white'
                        : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: tag.color || '#6366f1' }}
                      />
                      <span className="text-sm">{tag.name}</span>
                    </div>
                    {selectedTags.includes(tag.id) && (
                      <Check size={16} className="text-red-400" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 border-t border-zinc-700 flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} className="border-zinc-600 text-zinc-300">
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          className="bg-red-500 hover:bg-red-600 text-white"
          disabled={selectedTags.length === 0}
        >
          Save Action
        </Button>
      </div>
    </WorkflowStepEditorShell>
  );
};

// =====================================================
// INTERNAL NOTIFICATION EDITOR
// =====================================================
export const InternalNotificationEditor = ({ isOpen, onClose, step, onSave, token , width, overlayMode, onOverlayDismiss, activePanelType = 'notification', chromeHeight }) => {
  const [notificationType, setNotificationType] = useState(step?.config?.notificationType || 'in_app');
  const [message, setMessage] = useState(step?.config?.message || '');
  const [actionName, setActionName] = useState(step?.config?.actionName || 'Internal Notification');

  useEffect(() => {
    if (step) {
      setNotificationType(step.config?.notificationType || 'in_app');
      setMessage(step.config?.message || '');
      setActionName(step.config?.actionName || 'Internal Notification');
    }
  }, [step]);

  if (!isOpen || !step) return null;

  const handleSave = () => {
    onSave({
      ...step,
      config: {
        ...step.config,
        notificationType,
        message,
        actionName,
        configured: message.length > 0,
      },
    });
    onClose();
  };

  const notificationTypes = [
    { value: 'in_app', label: 'In-App Notification', description: 'Show notification in platform', available: true },
    { value: 'email', label: 'Email', description: 'Send email notification', available: true },
    { value: 'sms', label: 'SMS', description: 'Send SMS notification', available: false },
    { value: 'whatsapp', label: 'WhatsApp', description: 'Send WhatsApp message', available: false },
  ];

  return (
    <WorkflowStepEditorShell
      isOpen={isOpen}
      width={width}
      overlayMode={overlayMode}
      onOverlayDismiss={onOverlayDismiss}
      activePanelType={activePanelType}
      chromeHeight={chromeHeight}
      className={DARK_STEP_SHELL_CLASS}
      testId="internal-notification-editor"
    >
      <div className="p-4 border-b border-zinc-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
            <Bell size={20} className="text-cyan-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Internal Notification</h3>
            <p className="text-xs text-zinc-500">Notify team members</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
          <X size={18} className="text-zinc-400" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Action Name */}
        <div>
          <Label className="text-zinc-400 text-sm">Action Name</Label>
          <Input
            value={actionName}
            onChange={(e) => setActionName(e.target.value)}
            placeholder="Internal Notification"
            className="mt-2 bg-zinc-800 border-zinc-700 text-white"
          />
        </div>

        {/* Notification Type */}
        <div>
          <Label className="text-zinc-400 text-sm">Notification Type *</Label>
          <div className="mt-2 space-y-2">
            {notificationTypes.map((type) => (
              <button
                key={type.value}
                onClick={() => type.available && setNotificationType(type.value)}
                disabled={!type.available}
                className={cn(
                  'w-full flex items-center justify-between p-3 rounded-lg border transition-all text-left',
                  !type.available && 'opacity-50 cursor-not-allowed',
                  notificationType === type.value
                    ? 'bg-cyan-500/10 border-cyan-500/50 text-white'
                    : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                )}
              >
                <div>
                  <p className="font-medium text-sm">{type.label}</p>
                  <p className="text-xs text-zinc-500">
                    {type.available ? type.description : 'Provider not configured'}
                  </p>
                </div>
                {notificationType === type.value && (
                  <Check size={18} className="text-cyan-400" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Message */}
        <div>
          <Label className="text-zinc-400 text-sm">Notification Message *</Label>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Enter notification message..."
            className="mt-2 bg-zinc-800 border-zinc-700 text-white min-h-[100px]"
          />
          <p className="text-xs text-zinc-500 mt-1">
            Use variables: {'{{contact.first_name}}'}, {'{{contact.email}}'}
          </p>
        </div>
      </div>

      <div className="p-4 border-t border-zinc-700 flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} className="border-zinc-600 text-zinc-300">
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          className="bg-cyan-500 hover:bg-cyan-600 text-white"
          disabled={!message}
        >
          Save Action
        </Button>
      </div>
    </WorkflowStepEditorShell>
  );
};

// =====================================================
// WAIT EDITOR
// =====================================================
export const WaitEditor = ({ isOpen, onClose, step, onSave , width, overlayMode, onOverlayDismiss, activePanelType = 'wait', chromeHeight }) => {
  const [duration, setDuration] = useState(step?.config?.duration || 1);
  const [unit, setUnit] = useState(step?.config?.unit || 'hours');
  const [actionName, setActionName] = useState(step?.config?.actionName || 'Wait');

  useEffect(() => {
    if (step) {
      setDuration(step.config?.duration || 1);
      setUnit(step.config?.unit || 'hours');
      setActionName(step.config?.actionName || 'Wait');
    }
  }, [step]);

  const draft = useMemo(() => ({ duration, unit, actionName }), [duration, unit, actionName]);
  const mapStepToDraft = useCallback((s) => ({
    duration: s?.config?.duration || 1,
    unit: s?.config?.unit || 'hours',
    actionName: s?.config?.actionName || 'Wait',
  }), []);
  const { requestClose, commitDraft } = useEditorCloseGuard(
    'wait',
    isOpen,
    step,
    draft,
    mapStepToDraft,
    onClose,
  );

  if (!isOpen || !step) return null;

  const handleSave = () => {
    onSave({
      ...step,
      config: {
        ...step.config,
        duration,
        unit,
        actionName,
        configured: true,
      },
    });
    commitDraft();
    onClose();
  };

  const getPreviewText = () => {
    const plural = duration > 1 ? 's' : '';
    return `Wait ${duration} ${unit}${plural}`;
  };

  return (
    <WorkflowStepEditorShell
      isOpen={isOpen}
      width={width}
      overlayMode={overlayMode}
      onOverlayDismiss={onOverlayDismiss}
      activePanelType={activePanelType}
      chromeHeight={chromeHeight}
      className={DARK_STEP_SHELL_CLASS}
      testId="wait-editor"
    >
      <div className="p-4 border-b border-zinc-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
            <Clock size={20} className="text-amber-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Wait / Delay</h3>
            <p className="text-xs text-zinc-500">Add a time delay</p>
          </div>
        </div>
        <button onClick={requestClose} className="p-2 hover:bg-zinc-800 rounded-lg">
          <X size={18} className="text-zinc-400" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Action Name */}
        <div>
          <Label className="text-zinc-400 text-sm">Action Name</Label>
          <Input
            value={actionName}
            onChange={(e) => setActionName(e.target.value)}
            placeholder="Wait"
            className="mt-2 bg-zinc-800 border-zinc-700 text-white"
          />
        </div>

        {/* Duration Configuration */}
        <div>
          <Label className="text-zinc-400 text-sm">Wait Duration *</Label>
          <div className="grid grid-cols-2 gap-4 mt-2">
            <div>
              <Input
                type="number"
                min="1"
                max="999"
                value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value) || 1)}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="minutes">Minutes</SelectItem>
                  <SelectItem value="hours">Hours</SelectItem>
                  <SelectItem value="days">Days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <p className="text-sm text-amber-300 font-medium">Preview</p>
          <p className="text-white mt-1">{getPreviewText()}</p>
          <p className="text-xs text-zinc-500 mt-2">
            The workflow will pause at this step and automatically resume after the wait period.
          </p>
        </div>
      </div>

      <div className="p-4 border-t border-zinc-700 flex justify-end gap-2">
        <Button variant="outline" onClick={requestClose} className="border-zinc-600 text-zinc-300">
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          className="bg-amber-500 hover:bg-amber-600 text-white"
          disabled={duration < 1}
        >
          Save Action
        </Button>
      </div>
    </WorkflowStepEditorShell>
  );
};

// =====================================================
// IF/ELSE CONDITION EDITOR
// =====================================================
export const ConditionEditor = ({ isOpen, onClose, step, onSave, token, agencyId , width, overlayMode, onOverlayDismiss, activePanelType = 'condition', chromeHeight }) => {
  const [conditions, setConditions] = useState(step?.config?.conditions || []);
  const [logic, setLogic] = useState(step?.config?.logic || 'and');
  const [actionName, setActionName] = useState(step?.config?.actionName || 'If/Else Condition');
  const [filterFields, setFilterFields] = useState([]);
  const [tags, setTags] = useState([]);
  const [loadingFields, setLoadingFields] = useState(true);

  useEffect(() => {
    if (step) {
      setConditions(step.config?.conditions || [{ field: '', operator: 'equals', value: '' }]);
      setLogic(step.config?.logic || 'and');
      setActionName(step.config?.actionName || 'If/Else Condition');
    }
  }, [step]);

  // Fetch filter fields and tags
  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      setLoadingFields(true);
      try {
        // Fetch filter fields
        const fieldsRes = await fetch(`${API_URL}/api/workflow-actions/filter-fields`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (fieldsRes.ok) {
          const data = await fieldsRes.json();
          setFilterFields(data.fields || []);
        }

        // Fetch tags for tag-based conditions
        const params = new URLSearchParams();
        if (agencyId) params.append('agency_id', agencyId);
        
        const tagsRes = await fetch(`${API_URL}/api/workflow-actions/tags?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (tagsRes.ok) {
          const data = await tagsRes.json();
          setTags(data.tags || []);
        }
      } catch (err) {
        console.error('Failed to fetch condition data:', err);
      } finally {
        setLoadingFields(false);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen, token, agencyId]);

  if (!isOpen || !step) return null;

  const operators = [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Does not equal' },
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Does not contain' },
    { value: 'is_empty', label: 'Is empty' },
    { value: 'is_not_empty', label: 'Is not empty' },
    { value: 'greater_than', label: 'Greater than' },
    { value: 'less_than', label: 'Less than' },
    { value: 'is_true', label: 'Is true' },
    { value: 'is_false', label: 'Is false' },
    { value: 'has_tag', label: 'Has tag' },
    { value: 'does_not_have_tag', label: 'Does not have tag' },
  ];

  const addCondition = () => {
    setConditions([...conditions, { field: '', operator: 'equals', value: '' }]);
  };

  const removeCondition = (index) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const updateCondition = (index, key, value) => {
    const newConditions = [...conditions];
    newConditions[index] = { ...newConditions[index], [key]: value };
    setConditions(newConditions);
  };

  const handleSave = () => {
    onSave({
      ...step,
      config: {
        ...step.config,
        conditions,
        logic,
        actionName,
        configured: conditions.length > 0 && conditions[0].field,
      },
    });
    onClose();
  };

  const getConditionSummary = () => {
    if (conditions.length === 0) return 'No conditions set';
    const validConditions = conditions.filter(c => c.field);
    if (validConditions.length === 0) return 'No conditions set';
    
    const parts = validConditions.map(c => {
      const field = filterFields.find(f => f.id === c.field);
      return field?.name || c.field;
    });
    
    return parts.join(logic === 'and' ? ' AND ' : ' OR ');
  };

  return (
    <WorkflowStepEditorShell
      isOpen={isOpen}
      width={width}
      overlayMode={overlayMode}
      onOverlayDismiss={onOverlayDismiss}
      activePanelType={activePanelType}
      chromeHeight={chromeHeight}
      className={DARK_STEP_SHELL_CLASS}
      testId="condition-editor"
    >
      <div className="p-4 border-b border-zinc-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <GitBranch size={20} className="text-purple-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">If / Else Condition</h3>
            <p className="text-xs text-zinc-500">Add conditional logic</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg">
          <X size={18} className="text-zinc-400" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Action Name */}
        <div>
          <Label className="text-zinc-400 text-sm">Action Name</Label>
          <Input
            value={actionName}
            onChange={(e) => setActionName(e.target.value)}
            placeholder="If/Else Condition"
            className="mt-2 bg-zinc-800 border-zinc-700 text-white"
          />
        </div>

        {/* Logic Type */}
        <div>
          <Label className="text-zinc-400 text-sm">When to execute "Yes" branch</Label>
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => setLogic('and')}
              className={cn(
                'flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all',
                logic === 'and'
                  ? 'bg-purple-500/20 border border-purple-500/50 text-purple-300'
                  : 'bg-zinc-800 border border-zinc-700 text-zinc-400 hover:border-zinc-600'
              )}
            >
              ALL conditions match
            </button>
            <button
              onClick={() => setLogic('or')}
              className={cn(
                'flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all',
                logic === 'or'
                  ? 'bg-purple-500/20 border border-purple-500/50 text-purple-300'
                  : 'bg-zinc-800 border border-zinc-700 text-zinc-400 hover:border-zinc-600'
              )}
            >
              ANY condition matches
            </button>
          </div>
        </div>

        {/* Conditions */}
        <div>
          <Label className="text-zinc-400 text-sm">Conditions</Label>
          <div className="space-y-3 mt-2">
            {loadingFields ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
              </div>
            ) : (
              conditions.map((condition, index) => (
                <div key={index} className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700 space-y-3">
                  {index > 0 && (
                    <div className="flex items-center justify-center">
                      <Badge variant="outline" className="bg-purple-500/20 border-purple-500/50 text-purple-300">
                        {logic === 'and' ? 'AND' : 'OR'}
                      </Badge>
                    </div>
                  )}
                  
                  {/* Field Selector */}
                  <Select
                    value={condition.field}
                    onValueChange={(v) => updateCondition(index, 'field', v)}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue placeholder="Select field..." />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700 max-h-[200px]">
                      {filterFields.map((field) => (
                        <SelectItem key={field.id} value={field.id}>
                          {field.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Operator Selector */}
                  <Select
                    value={condition.operator}
                    onValueChange={(v) => updateCondition(index, 'operator', v)}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {operators.map((op) => (
                        <SelectItem key={op.value} value={op.value}>
                          {op.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Value Input - varies by operator */}
                  {!['is_empty', 'is_not_empty', 'is_true', 'is_false'].includes(condition.operator) && (
                    condition.operator === 'has_tag' || condition.operator === 'does_not_have_tag' ? (
                      <Select
                        value={condition.value}
                        onValueChange={(v) => updateCondition(index, 'value', v)}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                          <SelectValue placeholder="Select tag..." />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700 max-h-[200px]">
                          {tags.map((tag) => (
                            <SelectItem key={tag.id} value={tag.name}>
                              {tag.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={condition.value}
                        onChange={(e) => updateCondition(index, 'value', e.target.value)}
                        placeholder="Enter value..."
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    )
                  )}

                  {/* Remove condition button */}
                  {conditions.length > 1 && (
                    <button
                      onClick={() => removeCondition(index)}
                      className="text-xs text-red-400 hover:text-red-300"
                    >
                      Remove condition
                    </button>
                  )}
                </div>
              ))
            )}

            {/* Add condition button */}
            <button
              onClick={addCondition}
              className="w-full flex items-center justify-center gap-2 py-2 border border-dashed border-zinc-700 rounded-lg text-zinc-400 hover:border-purple-500/50 hover:text-purple-300 transition-all"
            >
              <Plus size={16} />
              <span className="text-sm">Add Condition</span>
            </button>
          </div>
        </div>

        {/* Branch Preview */}
        <div className="space-y-2">
          <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
            <div className="flex items-center gap-2">
              <Check size={16} className="text-emerald-400" />
              <p className="text-sm text-emerald-300 font-medium">Yes Branch</p>
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              Executes when: {getConditionSummary()}
            </p>
          </div>
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <div className="flex items-center gap-2">
              <X size={16} className="text-red-400" />
              <p className="text-sm text-red-300 font-medium">No Branch</p>
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              Executes when conditions are not met
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 border-t border-zinc-700 flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} className="border-zinc-600 text-zinc-300">
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          className="bg-purple-500 hover:bg-purple-600 text-white"
          disabled={conditions.length === 0 || !conditions[0].field}
        >
          Save Action
        </Button>
      </div>
    </WorkflowStepEditorShell>
  );
};

export default {
  GrantPortalAccessEditor,
  RevokePortalAccessEditor,
  GrantOfferAccessEditor,
  RevokeOfferAccessEditor,
  ApplyTagEditor,
  RemoveTagEditor,
  InternalNotificationEditor,
  WaitEditor,
  ConditionEditor,
};
